import { useState, useEffect, useCallback } from "react";
import apiClient from "../services/api";
import { toast } from "sonner";

export const formatFileSize = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

export const useJobDocuments = (jobId) => {
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchDocuments = useCallback(async () => {
    if (!jobId) return;
    try {
      const [docsRes, optRes] = await Promise.all([
        apiClient.get(`/jobs/${jobId}/documents`),
        apiClient.get(`/settings/options`),
      ]);
      setDocuments(docsRes.data || []);
      setCategories(optRes.data.document_categories || []);
    } catch {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const uploadDocuments = async (files, category, description) => {
    if (!files?.length) return false;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      fd.append("category", category);
      if (description) fd.append("description", description);
      const res = await apiClient.post(`/jobs/${jobId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      const { count, errors = [] } = res.data;
      if (count) toast.success(`${count} document${count === 1 ? "" : "s"} uploaded`);
      errors.forEach((err) => toast.error(err));
      await fetchDocuments();
      return count > 0;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
      return false;
    } finally {
      setUploading(false);
    }
  };

  const updateDocument = async (docId, data) => {
    try {
      await apiClient.put(`/jobs/${jobId}/documents/${docId}`, data);
      toast.success("Document updated");
      await fetchDocuments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const deleteDocument = async (docId) => {
    try {
      await apiClient.delete(`/jobs/${jobId}/documents/${docId}`);
      toast.success("Document deleted");
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const openDocument = async (doc, download = false) => {
    try {
      const res = await apiClient.get(`/jobs/${jobId}/documents/${doc.id}/file`, {
        params: download ? { download: true } : {},
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      if (download) {
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.original_name || doc.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, "_blank", "noopener");
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.status === 424 ? "File is in OneDrive and OneDrive is not reachable right now" : "Could not open document");
    }
  };

  return { documents, categories, loading, uploading, uploadDocuments, updateDocument, deleteDocument, openDocument, refresh: fetchDocuments };
};
